import { hasSurfaceTypeValue } from '../../../shared/surface-geometry.js';
import { isOperatorStatus } from '../../statuses/riding-removal.js';
import { isSelfStatusOrigin } from '../../statuses/status-origin.js';
import { type JanusCtx, type Row, activeStatuses, actorId, ec, ecId, ecLean, handBack, insertRoll, isInventoryRow, itemField, record, requiredInt, upper, pendingStatusDefinitions, definitionNotHeld } from '../context.js';
import { inventoryDestroyedInFrame } from '../../inventory/destruction-statuses.js';
import { statusApplicableToTarget, statusQuantityAlias } from './inventory-status-applicability.js';
import { encounterField } from '../../../shared/array-types.js';
import { type JanusInput } from '../janus.js';
import { getStatusEffectDurationTimes, type StatusEnds } from './get-status-effect-duration-times.js';
import { SHAPES as AOE_SHAPES } from '../aoe/expand-placed-aoe-targets.js';
import { carrierHasTrigger } from './status-trigger-gate.js';
import { runTriggerFamily, triggerFamilyPreDraw } from '../trigger-executor.js';

const GAINS_STATUS = 'triggers_when_caster_gains_status_effect';
const TARGET_GAINS_STATUS = 'triggers_when_target_gains_status_effect';
import { statusForcedRollExtreme } from './forced-roll-extreme.js';
import { removeStoredStatus } from './status-effect-removal-helpers.js';
import { checkOpposingTeam } from '../check-opposing-team.js';
import { STATUS_IDS, statusItemId as heldStatusItemId } from '../../statuses/expiry.js';
import { plCatalogs } from '../../pl/contract.js';
import { dropDyingHands } from '../../death/compose.js';
import { janusSizeGatedDefenseBlockingSource } from '../../port/k3-rules/size_gated_defenses.js';
import { browserDependencies } from '../../port/browser-dependencies.js';
import { readLayerListEntryUuid } from '../../compile/inventory-raw-readers.js';
import { readCreaturePayloadStatuses } from '../../compile/creature-status-reader.js';
import { resolveConnectionCascade } from '../../statuses/gm-boundary.js';
import { DEAD_STATUS_ID, UNCONSCIOUS_STATUS_ID } from '../../death/state-ids.js';
import { stageRowFamily, planStageRowApplication } from '../../statuses/stage-rows.js';
import { resolveExactRemovalTails } from '../../statuses/exact-removal-tails.js';
import { resolveInstantDeath } from '../rolls/damage-type-roll.js';

const END_ON_GAIN = 'effect_ends_when_target_gains_status_effect';
/** R-173 (2026-10-07, Psion Metamorph L5 "Accelerated Regeneration" 7EBCEF7E…: "ends early if the target becomes Unconscious"):
 * effect_ends_when_target_gains_status_effect — the target just GAINED `gained`; every OTHER row it holds whose own item (or origin item, for a
 * rider) lists that status ends with its loss hooks and a `status_effect_expired` row.
 * Becoming Unconscious releases the complete concentration connection, including object effects. */
export function endEffectsOnStatusGain(ctx: JanusCtx, target: Row, gained: string, gainedRow: unknown): void {
  for (const status of activeStatuses(ctx, target)) {
    if ((status as unknown) === gainedRow || ctx.statusesRemoved.some(row => upper(row.status_id) === upper(ctx.read.value(status, 'status_id')))) continue;
    const inline = record(status.status_arr);
    const item = upper(status.status_item_id ?? itemField(inline, 'item_id'));
    if (gained === UNCONSCIOUS_STATUS_ID && item === STATUS_IDS.concentrating) {
      resolveConnectionCascade(ctx, status, ecId(target));
      insertRoll(ctx, { roll_type_1: 'concentration_broken', roll_item_id: item, by: target });
      continue;
    }
    const origin = isSelfStatusOrigin(upper(status.status_id), upper(status.status_origin_id)) ? '' : upper(status.status_origin_id);
    let ends = false;
    for (const id of [...new Set([item, origin])]) {
      if (!id) continue;
      // the row's own item: the catalog, else the inline row (the same resolution — and the same hand-back — as the existing-status
      // loop above, so this adds no new one); a rider's ORIGIN only when the page holds it (the PHP copy reads it on the server paths)
      const definition = ctx.read.definition(id) ?? (id === item ? inline : null);
      if (!definition) { if (id === item) definitionNotHeld('apply_status_effect:end-on-gain:definition-not-held', id); continue; }
      if (ctx.read.entries(definition, END_ON_GAIN).some((entry) =>
        upper(typeof entry === 'string' ? entry.split('|')[0] : record(entry)?.item_layer_effect ?? itemField(record(entry), 'item_id')) === gained)) { ends = true; break; }
    }
    if (!ends) continue;
    // A row THIS use added would go through removePendingRow, which also culls a same-connection peer (the gained row itself can be one) —
    // PHP removes the exact row only (skip_connected_peer_cull) → PHP (Codex review 2026-10-07 P1).
    if (status.tick_added || ctx.statusesAdded.includes(status as never)) handBack('apply_status_effect:end-on-gain:pending-row');
    removeStoredStatus(ctx, target, status);
    insertRoll(ctx, { roll_type_1: 'status_effect_expired', roll_item_id: item, by: target, to: null });
  }
}

/** StatusEffect::QUANTITY_GROUPS by canonical base UUID (getQuantityInfo(): the catalog names drift — the mirror-image base is
 * "Mirror Images"). maimed's 10th stack applies its overflow status (dead): unported → hand back. */
export const QUANTITY_FAMILIES: Readonly<Record<string, { max: number; overflow: boolean }>> = {
  '09FC38BC-6060-4B67-A33F-EF7D448F71F1': { max: 10, overflow: false }, // burning
  'F632281D-2F16-FFDA-FB95-2E958988F01F': { max: 10, overflow: false }, // corroding
  '3BE713AB-37D7-630B-C1F8-9470784BA33E': { max: 10, overflow: false }, // bleeding
  'A5CDC77C-D224-DD5F-291B-EF8DF2EA443D': { max: 9, overflow: true },   // maimed
  'CDC5CFDA-B09C-4A68-820D-6C74D90BD595': { max: 3, overflow: false },  // mirror_image_effect
  'CEF623E6-6A71-37FD-29C7-12988B43151A': { max: 10, overflow: false }, // arcane_fatigue
  '4744C91C-51DC-4C36-8C6E-DB801410FF61': { max: 10, overflow: false }, // blighted
  'EB6632D3-5EC0-4E4D-5C6C-1BCB7EF57022': { max: 10, overflow: false }, // shielded
};
/** apply_status_effect's quantity block → janus_add_status_stack(): ONE row per family, re-application adds a stack (capped);
 * the `status_stack_increased` row is logged only when the count actually moved. */
function addQuantityStack(ctx: JanusCtx, id: string, same: Row[], caster: Row, target: Row, delta = 1, logRow = true): void {
  if (!ctx.statusStacks) handBack('apply_status_effect:quantity-increment');
  // Reapplications in this use (including critical + dying Maimed) share one pending family row.
  // Persist its final count once and retain each native status_stack_increased log.
  if (same.length === 1 && (ctx.statusesAdded.includes(same[0] as never) || ctx.priorDispatchStatuses?.has(same[0] as never))) {
    const row = same[0] as unknown as { status_stack?: number };
    const family = QUANTITY_FAMILIES[id];
    if (!family) handBack('apply_status_effect:quantity-family-unknown');
    const old = requiredInt(row.status_stack ?? 1, 'apply_status_effect:quantity-stack-not-held');
    if (family!.overflow && old + delta > family!.max) handBack('apply_status_effect:quantity-overflow');
    const next = Math.min(family!.max, Math.max(1, old + delta));
    row.status_stack = next;
    if (next !== old && logRow) insertRoll(ctx, { roll_type_1: 'status_stack_increased', roll_item_id: id, by: caster, to: target });
    return;
  }
  const stored = same.filter((row) => !ctx.statusesAdded.includes(row as never));
  if (stored.length !== same.length || stored.length !== 1) handBack('apply_status_effect:quantity-increment-row-ambiguous');
  const row = stored[0]!;
  const statusId = upper(row.status_id);
  if (!/^[0-9A-F]{8}(-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(statusId)) handBack('apply_status_effect:quantity-row-id-not-held');
  const family = QUANTITY_FAMILIES[id];
  if (!family) handBack('apply_status_effect:quantity-family-unknown');
  const max = family!.max;
  const pending = ctx.statusStacks!.find((change) => change.status_id === statusId);
  const before = pending ? pending.before : requiredInt(row.status_stack ?? 1, 'apply_status_effect:quantity-stack-not-held');
  const old = pending ? pending.status_stack : before;
  if (family!.overflow && old + delta > max) handBack('apply_status_effect:quantity-overflow');
  const next = Math.min(max, Math.max(1, old + delta));
  if (pending) pending.status_stack = next;
  else ctx.statusStacks!.push({ status_id: statusId, status_item_id: id, target: actorId(target), before, status_stack: next });
  if (next !== old && logRow) insertRoll(ctx, { roll_type_1: 'status_stack_increased', roll_item_id: id, by: caster, to: target });
}

/** A layer cell's truthiness (a bare scalar or its [0] wrapper). */
function cellTruthy(value: unknown): boolean {
  let v = value;
  while (Array.isArray(v)) v = v[0];
  const r = record(v);
  if (r && '0' in r) v = r['0'];
  return v !== null && v !== undefined && v !== false && v !== '' && v !== '0' && v !== 0;
}
/** immune_to_size_reduction on a creature: its own row (root / carriers), an eligible held passive (an aura or combat-start TIMED copy
 *  reaches it as a status row instead), a feat, or an active status (or a passive the status attaches). */
export function holdsSizeReductionImmunity(ctx: JanusCtx, target: Row): boolean {
  const LAYER = 'immune_to_size_reduction';
  if (cellTruthy(ctx.read.value(target, LAYER))) return true;
  const defOf = (entry: unknown): Row | null => {
    const inner = record(record(entry)?.item_arr) ?? (record(entry)?.item_layer_types ? record(entry) : null);
    if (inner) return inner;
    const id = upper(typeof entry === 'string' ? entry : record(entry)?.item_id ?? record(entry)?.ability_id);
    return id ? ctx.read.definition(id) : null;
  };
  const held = (def: Row, name: string): boolean => ctx.read.value(def, name) != null;
  const deliveredAsStatus = (def: Row): boolean => ['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target'].every(n => held(def, n))
    || (held(def, 'triggers_after_combat_begins') && held(def, 'duration_type'));
  for (const entry of ctx.read.entries(target, 'passive_ability')) {
    if (record(entry)?.passive_conditions_active === false) continue;
    const def = defOf(entry);
    if (def && !deliveredAsStatus(def) && cellTruthy(ctx.read.value(def, LAYER))) return true;
  }
  for (const entry of ctx.read.entries(target, 'feat')) { const def = defOf(entry); if (def && cellTruthy(ctx.read.value(def, LAYER))) return true; }
  for (const status of activeStatuses(ctx, target)) {
    const def = ctx.read.definition(upper(status.status_item_id)) ?? record(status.status_arr);
    if (!def) continue;
    if (cellTruthy(ctx.read.value(def, LAYER))) return true;
    for (const p of ctx.read.entries(def, 'passive_ability')) { const pd = defOf(p); if (pd && cellTruthy(ctx.read.value(pd, LAYER))) return true; }
  }
  return false;
}

/** Bind the existing size defense decision to the native action's resolved carriers. Numeric
 * presence matters: N=0 means immunity against a creature of the same size or smaller. */
export function sizeDefenseBlocks22(ctx: JanusCtx, target: Row, caster: Row,
  layer = 'immune_to_status_effect_prone_from_size_category_number_below'): boolean {
  const sources: Row[] = [], seen = new Set<Row>(), seenIds = new Set<string>();
  const visit = (entry: unknown): void => {
    const wrapper = record(entry);
    if (wrapper?.passive_conditions_active === false) return;
    const def = record(wrapper?.item_arr) ?? (wrapper?.item_layer_types || wrapper?.inventory ? wrapper : null)
      ?? ctx.read.definition(upper(readLayerListEntryUuid(entry)));
    if (!def || seen.has(def)) return;
    const identity = upper(ctx.read.inventoryField?.(def, 'inventory_id') ?? itemField(def, 'item_id'));
    if (identity && seenIds.has(identity)) return;
    if (identity) seenIds.add(identity);
    seen.add(def);
    if (Object.keys(record(def.item_layer_types) ?? {}).some(key => key.startsWith('triggers_'))) return;
    const raw = ctx.read.value(def, layer);
    if ((typeof raw === 'number' || typeof raw === 'string' && raw.trim() !== '') && Number.isFinite(Number(raw))) sources.push({ value: Number(raw) });
    for (const child of ctx.read.entries(def, 'passive_ability')) visit(child);
  };
  for (const key of ['creature_type_arr', 'creature_sub_type_arr', 'lineage_arr', 'class_arr', 'subclass_arr', 'background_arr'])
    for (const entry of Object.values(record(target[key]) ?? {})) visit(entry);
  for (const key of ['feat', 'passive_ability', 'full_ability', 'ability', 'quick_ability', 'attack_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability'])
    for (const entry of ctx.read.entries(target, key)) visit(entry);
  const payload = readCreaturePayloadStatuses({ ...target, statuses: { target: activeStatuses(ctx, target) } },
    (row, key) => ctx.read.value(row as Row, key), ctx.read.definition, id => ctx.members.get(upper(id)) ?? null);
  for (const status of Object.values(payload) as Row[]) if (status.passive_conditions_active !== false)
    visit(record(status.status_arr) ?? ctx.read.definition(upper(status.status_item_id)));
  for (const entry of ctx.read.entries(target, 'inventory')) {
    const item = record(entry);
    if (item && ctx.read.inventoryField?.(item, 'equipped')) visit(item);
  }
  const normalized = (row: Row): Row => ({ ...row, encounter_creatures: { ...ec(row), encounter_creature_id: ecId(row) } });
  return janusSizeGatedDefenseBlockingSource(normalized(target), normalized(caster), layer, browserDependencies({
    resolve_adv_dis_layer_sources: () => sources,
    layer_value: (row, key) => key === 'size' ? readLayerListEntryUuid(ctx.read.value(row as Row, key)) : ctx.read.value(row as Row, key),
    item_field: (row, key) => itemField(row as Row, key),
    get_target_type: row => isInventoryRow(row as Row) ? 'inventory' : String(ecLean(row as Row, 'item_type') ?? itemField(row as Row, 'item_type') ?? ''),
  })) !== null;
}
/** A hostile shrink on an immune target: the status's change_creature_size is a proven negative integer, the caster is another creature on
 *  the OPPOSING team (check_opposing_team — an unknown team never blocks), and the target holds the immunity. */
function sizeReductionBlocked(ctx: JanusCtx, definition: Row, caster: Row, target: Row): boolean {
  const deltas = ctx.read.entries(definition, 'change_creature_size').map((entry) => {
    let v: unknown = entry; const r = record(v); if (r) v = r.item_layer_effect ?? r['0'];
    const s = String(v ?? '').trim();
    return /^[+-]?\d+$/.test(s) ? Number(s) : NaN;
  });
  if (!deltas.some((d) => d < 0)) return false;
  const casterId = actorId(caster), targetId = actorId(target);
  if (!casterId || casterId === targetId || !checkOpposingTeam(ctx, caster, target)) return false;
  return holdsSizeReductionImmunity(ctx, target);
}

/** Resolve immunity before callers perform any work belonging to the skipped status. */
export function skipImmuneStatus(ctx: JanusCtx, id: string, definition: Row, caster: Row, target: Row,
  logRow = true, deathPosture = false): boolean {
  const immuneIds = ctx.read.entries(target, 'immune_status_effect').map((entry) =>
    upper(typeof entry === 'string' ? entry : itemField(record(entry), 'item_id')));
  const defenses = record(record(record(target.computed)?.creature)?.defenses);
  const projectedImmune = Array.isArray(defenses?.immune_status) ? defenses.immune_status : [];
  const bypassImmunity = deathPosture && id === 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA' && caster === target;
  // Canonical category IDs do not require unrelated definitions to be held.
  const DISEASED = '4638F1B5-48F9-4E81-95B7-E0325F40B5EF';
  const DROWNING = '1E8008FA-B68A-8DE8-48CE-0EB121008FEB';
  const SUFFOCATING = '2A20ACDF-3BC6-B964-8FBD-F8E1AC581AA5';
  const blocked = !bypassImmunity && (projectedImmune.some(grant => upper(record(grant)?.item_layer_effect) === id)
    || ctx.read.has(definition, 'immune_to_status_effect') || immuneIds.includes(id)
    || ctx.read.has(target, 'immune_to_status_effect')
    || String(itemField(definition, 'item_type')) === 'disease' && immuneIds.includes(DISEASED)
    || id === DROWNING && immuneIds.includes(SUFFOCATING)
    || String(itemField(definition, 'item_name') ?? '').toLowerCase() === 'prone' && sizeDefenseBlocks22(ctx, target, caster))
    // Hostile magical shrinking also respects the holder's size-reduction immunity.
    || !isInventoryRow(target) && sizeReductionBlocked(ctx, definition, caster, target);
  if (blocked && logRow) insertRoll(ctx, { roll_type_1: 'target_immune_to_status_effect', roll_item_id: id, by: caster, to: target });
  return blocked;
}

/** includes/functions/statuses/apply_status_effect_inc.php:apply_status_effect */
export function applyStatusEffect(ctx: JanusCtx, input: JanusInput, ability: Row, statusItemId: string, rollType: string, caster: Row, target: Row,
  /** apply_player_dying.php's own three calls pass a literal `status_ends: 0` — Crippled/Dying/Maimed's
   * duration is NEVER the casting ability's (a Wall of Light's own `duration_int` has nothing to do with how
   * long its victim stays Dying). Omitted, this still derives from the ability exactly as every other caller
   * needs (getStatusEffectDurationTimes). A capture-exposed divergence (2026-09-26): PC-dying statuses carried
   * the casting spell's own duration instead of the permanent `0-0-0-0-0-0` PHP always writes for them. */
  forcedEnds?: StatusEnds,
  /** Give a lingering tail its own connection: healing Dying leaves Maimed standing, and leaving a Haste zone leaves Stunned
   * until its next-turn expiry. Other applications share the use's connection. */
  ownConnection = false,
  /** apply_status_effect's `log_message: false` (counterspell_apply_tier_lockout logs its own tier line instead). */
  logRow = true,
  /** apply_status_effects' stacks_applied (the RIDER path only — every other caller passes PHP's default 0): a quantity family adds this many
   *  stacks (0 = 1 for a base id); ignored for any other status (lane A8, R-130 Huecuva — was a hand-back). */
  stacksApplied = 0,
  /** Wildshift's Spell Locked row must outlive an unrelated counterspell lock. */
  allowDuplicateStatusItemId = false,
  /** Shapeshift form/lock rows have no origin pointer in PHP. */
  statusOriginId?: string | null,
  /** Native death posture passes bypass_immunity:true for its self-cast Prone only. */
  deathPosture = false, deathStage = false): void {
  const creatureId = ecId(target);
  if (isInventoryRow(target) && inventoryDestroyedInFrame(ctx, actorId(target))) return;
  const alias = statusQuantityAlias(statusItemId);
  const id = alias?.id ?? upper(statusItemId);
  if (alias && stacksApplied <= 0) stacksApplied = alias.stacks;
  const definition = ctx.read.definition(id);
  if (!definition) definitionNotHeld('apply_status_effect:definition-not-held', id);
  // Quantity families share one count; stage families retain their distinct rows.
  const statusName = String(itemField(definition, 'item_name') ?? '').toLowerCase();
  if (/^(?:judgement(?: [123])?|dying state(?: [123])?|recovery state(?: [123])?)$/.test(statusName)
      && !stageRowFamily(id))
    handBack('apply_status_effect:leveled-row-family');
  const quantityBase = id in QUANTITY_FAMILIES;
  if (statusName === 'melting' || /^(?:burning|corroding|bleeding|maimed|mirror image effect|arcane fatigue|blighted|shielded)\s*\(.*\)$/.test(statusName))
    handBack('apply_status_effect:quantity-alias');
  const objectTarget = isInventoryRow(target);
  if (!statusApplicableToTarget(ctx.read, id, definition, target)) {
    if (logRow) insertRoll(ctx, { roll_type_1: 'invalid_target_for_status_effect', roll_item_id: id, by: caster, to: target });
    return;
  }
  const targetId = actorId(target) || upper(encounterField(target, 'encounter_id'));
  if (!targetId) handBack('apply_status_effect:target-identity-not-held');
  if (skipImmuneStatus(ctx, id, definition, caster, target, logRow, deathPosture)) return;
  // A later immune request is still logged; other effects do not reapply to this completed corpse.
  if (creatureId && ctx.composedDeaths?.includes(creatureId)) return;
  if (creatureId && id === DEAD_STATUS_ID) {
    if (resolveInstantDeath(ctx, ability, caster, target) && logRow)
      insertRoll(ctx, { roll_type_1: rollType, roll_item_id: id, by: caster, to: target });
    return;
  }
  const existing = activeStatuses(ctx, target);
  for (const row of existing) {
    const other = ctx.read.definition(String(row.status_item_id ?? '')) ?? record((row as Row).status_arr);
    if (!other) handBack('apply_status_effect:unmodelled:existing-status-definition');
    for (const key of ['status_effect_replaced_by', 'replaces_status_effect', 'status_effect_stack',
      'status_effect_concentration'])
      if (ctx.read.has(other, key) && !(key === 'status_effect_stack' && stageRowFamily(heldStatusItemId(row))))
        handBack(`apply_status_effect:unmodelled:existing-status.${key}`);
  }
  for (const key of ['status_effect_replaced_by', 'replaces_status_effect', 'status_effect_stack',
    'status_effect_concentration'])
    if (ctx.read.has(definition, key) && !(key === 'status_effect_stack' && stageRowFamily(id)))
      handBack(`apply_status_effect:unmodelled:incoming-status.${key}`);
  if (!deathStage && stageRowFamily(id)) {
    const plan = planStageRowApplication(ctx, target, id);
    if (!plan) return;
    if (!ctx.read.definition(plan.itemId)) definitionNotHeld('status-stage:definition-not-held', plan.itemId);
    if (plan.previous) {
      resolveExactRemovalTails(ctx, target, plan.previous);
      removeStoredStatus(ctx, target, plan.previous, 'single');
    }
    applyStatusEffect(ctx, input, ability, plan.itemId, rollType, caster, target, forcedEnds,
      ownConnection, logRow, stacksApplied, allowDuplicateStatusItemId, statusOriginId,
      deathPosture, true);
    return;
  }
  if (quantityBase) {
    // janus_find_quantity_status_row() matches the family through its legacy ALIAS rows too (Melting on a Burning apply);
    // an alias row still held would be the one PHP stacks onto, so any same-family row under another id hands back.
    const aliasHeld = existing.some((row) =>
      statusQuantityAlias(upper(row.status_item_id ?? itemField(record((row as Row).status_arr), 'item_id')))?.id === id);
    if (aliasHeld) handBack('apply_status_effect:quantity-alias-row-held');
  }
  // Each concentration spell has its own connection; the cast gate already enforces the caster's cap.
  const concentration = id === STATUS_IDS.concentrating && rollType === 'spell_concentration';
  const same = existing.filter((row) => heldStatusItemId(row) === id
    && (!concentration || ctx.statusConnectedId != null && upper(ctx.read.value(row, 'status_connected_id')) === upper(ctx.statusConnectedId)));
  if (same.length && !allowDuplicateStatusItemId) {
    // apply_status_effect_inc.php: a creature already holding this item returns
    // before insertion. Contact re-dispatches keep that same duplicate guard;
    // the persistent anchor is placed separately and never re-created here.
    if (!concentration && !hasSurfaceTypeValue(ctx.read.value(ability, 'surface_type')) && ctx.read.has(ability, 'persistent_aoe') && !['moving_into_p_aoe', 'end_turn_in_p_aoe'].includes(String(input.useAbilityVariable1)))
      handBack('apply_status_effect:existing-persistent-status');
    // PHP clamps the incoming count to the family maximum BEFORE its overflow test (Codex A8 r3 P2); log_message passes through
    if (quantityBase) addQuantityStack(ctx, id, same, caster, target, Math.min(QUANTITY_FAMILIES[id]!.max, Math.max(1, stacksApplied)), logRow);
    return;
  }
  const ends = forcedEnds ?? getStatusEffectDurationTimes(ctx, ability, caster);
  const [targetStart, targetEnd, casterStart, casterEnd] = ends.flags;
  // apply_status_effect_inc.php reads geometry from the status ITEM, with
  // zero defaults for an ordinary occupant row. The same-item status has no
  // separate origin; a rider or Concentrating points to its applier.
  const dimension = (key: string): number => {
    const override = id === input.abilityId ? record(ability.computed)?.[key] : null;
    const value = override ?? ctx.read.value(definition, key);
    if (value == null || value === '') return 0;
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || Math.abs(parsed) > 1000000) handBack(`apply_status_effect:invalid-${key}`);
    if (key === 'aoe_distance' && record(ability.computed)?.ft_self_aoe_size_adjusted) return parsed;
    if (key !== 'aoe_distance' || id !== input.abilityId
        || upper(ctx.read.value(ability, 'range')) !== 'D82855E1-4BB9-93EA-6374-B9904318BC1D') return parsed;
    const shapeId = upper(ctx.read.value(definition, 'aoe_type'));
    if (!shapeId) return parsed;
    // AoeType::getVariableName(): the seven AoeType ids are constants (SHAPES); a page need not hold their catalog rows.
    const shape = AOE_SHAPES[shapeId] ?? String(itemField(ctx.read.definition(shapeId), 'item_name') ?? '').toLowerCase();
    if (!shape) handBack('apply_status_effect:self-aoe-shape-not-held');
    if (!['sphere', 'circle', 'cylinder', 'cube', 'square'].includes(shape)) return parsed;
    if (ctx.read.has(caster, 'swarm')) handBack('apply_status_effect:swarm-size-not-held');
    const width = record(record(record(caster.computed)?.creature)?.size)?.base;
    if (width == null || !Number.isFinite(Number(width))) handBack('apply_status_effect:self-aoe-size-not-held');
    const credit = Math.max(0, (Number(width) - 5) / 2);
    return Math.max(0, parsed) + (shape === 'cube' || shape === 'square' ? credit * 2 : credit);
  };
  const aoeType = ctx.read.value(definition, 'aoe_type');
  // Decide the snapshot before creating the row; a late entrant inherits the same-item connection's stamp,
  // including lean zero. The commit stores this decided column unchanged.
  const forcedRollExtreme = statusForcedRollExtreme(ctx, definition, caster, aoeType);
  // janus_trigger status-gain arms (apply_status_effect_inc.php ~L963, lane RX 2026-09-28): the GAINER's
  // triggers_when_caster_gains_status_effect (inciting: the applier), then — unless self-apply — the APPLIER's
  // triggers_when_target_gains_status_effect (inciting: the gainer), each decided by the RX twin and executed through janus() in
  // trigger mode (trigger-executor.ts). A carrier the executor cannot run, or an arm owner outside the member map, → PHP.
  const gainer = ctx.members.get(targetId);
  const applier = actorId(caster) ? ctx.members.get(actorId(caster)) : undefined;
  const gainArm = carrierHasTrigger(ctx, target, GAINS_STATUS, 'apply_status_effect:gain-trigger', id);
  const applierArm = actorId(caster) !== '' && actorId(caster) !== targetId && carrierHasTrigger(ctx, caster, TARGET_GAINS_STATUS, 'apply_status_effect:gain-trigger', id);
  if ((gainArm && !gainer) || (applierArm && !applier)) handBack('apply_status_effect:unmodelled:status-gain-trigger');
  if (gainArm) triggerFamilyPreDraw(ctx, gainer!, { trigger: GAINS_STATUS, statusItemId: id });
  if (applierArm) triggerFamilyPreDraw(ctx, applier!, { trigger: TARGET_GAINS_STATUS, statusItemId: id });
  // Owner 2026-10-11: grappling a rider or pilot ends its transport link; grappling the carrier does not.
  if (!objectTarget && id === STATUS_IDS.grappled) {
    for (const riding of activeStatuses(ctx, target).filter(isOperatorStatus)) {
      removeStoredStatus(ctx, target, riding);
      insertRoll(ctx, { roll_type_1: 'self_status_effect_deleted', roll_item_id: heldStatusItemId(riding), by: target, to: null });
    }
  }
  ctx.statusesAdded.push({
    status_item_id: id, target: targetId, caster: actorId(caster), status_connected_id: ownConnection ? ctx.read.uuid() : ctx.statusConnectedId ??= ctx.read.uuid(),
    status_stack: quantityBase ? Math.min(QUANTITY_FAMILIES[id]!.max, Math.max(1, stacksApplied)) : 1,
    status_ends_on_target_start_of_turn: targetStart, status_ends_on_target_end_of_turn: targetEnd,
    status_ends_on_caster_start_of_next_turn: casterStart, status_ends_on_caster_end_of_next_turn: casterEnd,
    ends: ends.ends,
    status_aoe_type: aoeType == null || aoeType === '' ? null : upper(aoeType),
    status_aoe_distance: dimension('aoe_distance'), status_aoe_distance_depth: dimension('aoe_distance_depth'),
    status_aoe_distance_height: dimension('aoe_distance_height'), status_aoe_source: 0,
    status_origin_point_x: 0, status_origin_point_y: 0, status_origin_point_z: 0,
    status_distance_point_x: 0, status_distance_point_y: 0, status_distance_point_z: 0,
    status_origin_id: statusOriginId !== undefined ? statusOriginId : id === input.abilityId ? null : input.abilityId,
    status_casting_targets_arr: null, status_opposing_team_only: 0, status_damage_type: null,
    status_forced_roll_extreme: forcedRollExtreme,
  });
  // apply_status_effect_inc.php fires the gain arms (~L968–1000) BEFORE its own log row (insert_log_message at the end, ~L1135) — lane A6
  const gainedRow = ctx.statusesAdded[ctx.statusesAdded.length - 1]!;
  pendingStatusDefinitions.set(gainedRow, definition);
  // Only a newly gained Sleeping row carries these tails: immunity and duplicate returns above apply to the whole effect.
  if (id === '095BC6BC-06F6-74E0-2B8A-FC1F32AF809B') {
    applyStatusEffect(ctx, input, ability, 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA', 'self_status_effect_applied', target, target,
      { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] });
    if (creatureId) dropDyingHands(ctx, target);
  }
  // (2026-10-02): the paoe-entry oracle's Unyielding Command (gains Dazed → strips it) logs its dispatch ahead of the Dazed row in PHP.
  if (gainArm) runTriggerFamily(ctx, { trigger: GAINS_STATUS, statusItemId: id }, gainer!, applier ?? caster, [gainer!], false);
  if (applierArm) runTriggerFamily(ctx, { trigger: TARGET_GAINS_STATUS, statusItemId: id }, applier!, gainer!, [applier!], false);
  // R-173: a held row that ends when its holder gains this status (apply_status_effect_inc.php, after the gain arms, before the log row).
  endEffectsOnStatusGain(ctx, target, id, gainedRow);
  if (logRow) insertRoll(ctx, { roll_type_1: rollType, roll_item_id: id, by: caster, to: target });
  // apply_status_effect_inc.php applies these two economy locks immediately to an
  // in-combat target, before the next turn's ordinary AP/reaction reset.
  if (!objectTarget && ctx.read.inCombat(ctx.encounter, target)) {
    if (ctx.read.has(definition, 'no_actions') && requiredInt(ctx.read.value(definition, 'no_actions'), 'apply_status_effect:invalid-no-actions')) ec(target).aps = 0;
    if (ctx.read.has(definition, 'no_reactions') && requiredInt(ctx.read.value(definition, 'no_reactions'), 'apply_status_effect:invalid-no-reactions')) ec(target).reaction = 0;
  }
}
